import {useRouter} from 'next/router';
import {useTranslation} from 'next-i18next';
import {useMemo} from 'react';

import {SEARCH_FILTERS} from '@/src/app/constants/search';
import useCitiesList from '@/src/hooks/useCitiesList';
import useCountriesList, {
  LocationType,
  LocationTypes,
} from '@/src/hooks/useCountriesList';
import useStatesLists from '@/src/hooks/useStatesLists';
import FilterRow from '@/src/Search/components/FilterRow';
import {useSearchFilters} from '@/src/Search/utils/useSearchFilters';
import TextLocal from '@/src/textLocal/containers/TextLocal';
import {SimpleCollapse, Text} from '@/src/ui';
import SimpleSelect from '@/src/ui/select/components/SimpleSelect';

interface optionProps {
  value: string | null;
  label: string;
}

interface locationProps {
  id: string;
}

export const getLocationOptions = (
  list: Array<locationProps>,
  locale: string
) => {
  return list.map((location) => ({
    label: location[`name_${locale}`],
    value: `${location.id}`,
  }));
};

const getSelectedLocation = (
  list: Array<optionProps>,
  query: string | string[]
) => {
  return list.find((location) => location.value === query);
};

interface LocationsSelectFilterProps {
  locationType: LocationType;
}

const LocationsSelectFilter = ({
  locationType = LocationTypes.ALL,
}: LocationsSelectFilterProps) => {
  const {t} = useTranslation('common');
  const {query, locale} = useRouter();
  // @ts-ignore
  const {setQueryFilters} = useSearchFilters();
  const {countriesList} = useCountriesList(locationType, query?.clinic_id);
  const {statesList} = useStatesLists(
    query[SEARCH_FILTERS.CLINIC_COUNTRIES],
    locationType,
    query?.clinic_id
  );
  const {citiesList} = useCitiesList(
    query[SEARCH_FILTERS.CLINIC_STATE],
    locationType,
    query?.clinic_id
  );

  const countriesListMemo = useMemo(
    () => getLocationOptions(countriesList, locale),
    [countriesList, locale]
  );

  const statesListMemo = useMemo(
    () => getLocationOptions(statesList, locale),
    [statesList, locale]
  );

  const citiesListMemo = useMemo(
    () => getLocationOptions(citiesList, locale),
    [citiesList, locale]
  );

  const selectedCountry = useMemo(
    () =>
      getSelectedLocation(
        countriesListMemo,
        query[SEARCH_FILTERS.CLINIC_COUNTRIES]
      ),
    [countriesListMemo, query[SEARCH_FILTERS.CLINIC_COUNTRIES]]
  );

  const selectedState = useMemo(
    () =>
      getSelectedLocation(statesListMemo, query[SEARCH_FILTERS.CLINIC_STATE]),
    [statesListMemo, query[SEARCH_FILTERS.CLINIC_STATE]]
  );

  const selectedCity = useMemo(
    () =>
      getSelectedLocation(citiesListMemo, query[SEARCH_FILTERS.CLINIC_CITY]),
    [citiesListMemo, query[SEARCH_FILTERS.CLINIC_CITY]]
  );

  return (
    <>
      <SimpleCollapse
        defaultIsOpen
        headTitle={
          <Text
            fontSize={Text.FONT_SIZE.SIZE_16}
            fontWeight={Text.FONT_WEIGHT.MEDIUM}
          >
            <TextLocal id={'profile.location'} />
          </Text>
        }
      >
        <FilterRow>
          <SimpleSelect
            placeholder={t('paymentDetails.country')}
            value={selectedCountry}
            options={countriesListMemo}
            onChange={({value}) => {
              return setQueryFilters({
                [SEARCH_FILTERS.CLINIC_COUNTRIES]: value,
                [SEARCH_FILTERS.CLINIC_STATE]: '',
                [SEARCH_FILTERS.CLINIC_CITY]: '',
                [SEARCH_FILTERS.OFFSET]: '0',
              });
            }}
          />
        </FilterRow>
        <FilterRow>
          <SimpleSelect
            placeholder={t('paymentDetails.state')}
            value={selectedState}
            options={statesListMemo}
            isDisabled={!selectedCountry}
            onChange={({value}) => {
              return setQueryFilters({
                [SEARCH_FILTERS.CLINIC_STATE]: value,
                [SEARCH_FILTERS.CLINIC_CITY]: '',
                [SEARCH_FILTERS.OFFSET]: '0',
              });
            }}
            noOptionsMessage={
              query[SEARCH_FILTERS.CLINIC_COUNTRIES]
                ? t('general.select-country-first')
                : null
            }
          />
        </FilterRow>
        <FilterRow>
          <SimpleSelect
            placeholder={t('paymentDetails.city')}
            value={selectedCity}
            options={citiesListMemo}
            isDisabled={!selectedState}
            onChange={({value}) => {
              return setQueryFilters({
                [SEARCH_FILTERS.CLINIC_CITY]: value,
                [SEARCH_FILTERS.OFFSET]: '0',
              });
            }}
            noOptionsMessage={
              query[SEARCH_FILTERS.CLINIC_STATE]
                ? t('general.select-state-first')
                : null
            }
          />
        </FilterRow>
      </SimpleCollapse>
    </>
  );
};

export default LocationsSelectFilter;
